import React, { useState, useCallback, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { AlertCircle, RefreshCw } from 'lucide-react';

const fmt = (v) =>
  Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const PeriodPnLChart = () => {
  const now = new Date();
  const yearStart = `${now.getFullYear()}-01-01`;
  const today = now.toISOString().slice(0, 10);

  const [startDate, setStartDate] = useState(yearStart);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/finance/period-summary', {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data);
    } catch {
      setError('Impossible de charger les données de la période.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = (data?.shipments || []).map((s) => ({
    name: s.name,
    date: s.departure_date ? new Date(s.departure_date).toLocaleDateString('fr-FR') : '',
    'Revenu cumulé': s.cum_revenue,
    'Charges cumulées': s.cum_charges,
    'Profit cumulé': s.cum_profit,
  }));

  const summary = data?.summary;

  return (
    <div className="space-y-4">
      {/* Date range controls */}
      <div className="flex items-end gap-3 flex-wrap">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Début</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Fin</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-40"
          />
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
          <RefreshCw size={14} className="mr-1" />
          Actualiser
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Chargement...</div>
      ) : (
        <>
          {/* Area chart */}
          {chartData.length > 0 && (
            <Card className="border-border">
              <CardHeader>
                <CardTitle className="text-base">Évolution cumulée — Revenus vs Charges</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}€`} />
                    <Tooltip formatter={(value) => fmt(value)} contentStyle={{ borderRadius: '8px', border: '1px solid #ECE7DC', fontSize: '13px' }} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Revenu cumulé"
                      stroke="#E8721A"
                      fill="#E8721A"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="Charges cumulées"
                      stroke="#15140F"
                      fill="#15140F"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="Profit cumulé"
                      stroke="#0E8855"
                      fill="#0E8855"
                      fillOpacity={0.08}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <SummaryCard label="Revenu total" value={fmt(summary.total_revenue)} />
              <SummaryCard label="Charges totales" value={fmt(summary.total_charges)} />
              <SummaryCard
                label="Profit"
                value={fmt(summary.total_profit)}
                color={summary.total_profit >= 0 ? 'green' : 'red'}
              />
              <SummaryCard label="Marge" value={`${summary.margin_pct}%`} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => {
  const cls = color === 'green'
    ? 'bg-green-50 border-green-200 text-green-700'
    : color === 'red'
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-muted border-border text-foreground';
  return (
    <div className={`border rounded-md p-3 ${cls}`}>
      <div className="text-xs mb-1 opacity-70">{label}</div>
      <div className="font-semibold text-base">{value}</div>
    </div>
  );
};

export default PeriodPnLChart;
